import React from 'react';
import ReactDOM from 'react-dom/client';
import { Provider, useSelector } from 'react-redux';
import { BrowserRouter } from 'react-router-dom';
import { PersistGate } from 'redux-persist/integration/react';
import reportWebVitals from './reportWebVitals';
import store, { persistedStore, RootState } from './store';

import { ThemeProvider } from '@emotion/react';
import { AuthenticationProvider } from '@lectra/react-authentication';
import { createTheme, responsiveFontSizes, Grid, Alert, Typography, Link, CssBaseline } from '@mui/material';
import ConnexionTabs from './home/ConnexionTabs';
import './index.scss';
import { createAppTheme } from './styles/LectraTheme';

interface PublicConfig {
    auth0Domain: string;
    auth0ClientId: string;
}


const ThemedApp: React.FC = () => {
    const themeMode = useSelector((state: RootState) => state.persistedThemePreferenceReducer.mode);
    let theme = createTheme(createAppTheme(themeMode));
    theme = responsiveFontSizes(theme);

    return (
        <BrowserRouter>
            <ThemeProvider theme={theme}>
                <CssBaseline />
                <ConnexionTabs />
            </ThemeProvider>
        </BrowserRouter>
    );
};

const App: React.FC<{ config: PublicConfig }> = ({ config }) => {

    /// Purge local storage
    // persistedStore.pause();
    // persistedStore.flush().then(() => {
    //   return persistedStore.purge();
    // });

    const reactAppSiteMode: string = process.env.REACT_APP_SITE_MODE ?? '';


    switch (reactAppSiteMode) {
        case "MAINTENANCE":
            return (
                <Grid container spacing={0} direction="column" alignItems="center" justifyContent="center"
                    sx={{ minHeight: '50vh' }}>
                    <Alert severity="info" sx={{
                        fontSize: '3rem', "& .MuiAlert-icon": {
                            fontSize: '3rem'
                        }
                    }} >
                            App is in Maintenance, duration around 5 minutes
                    </Alert>
                </Grid>
            );
        case "DISALLOWED":
            return (

                <Grid container spacing={0} direction="column"  alignItems="center" justifyContent="center"
                    sx={{ minHeight: '50vh' }}>
                    <Alert severity="warning" sx={{
                        fontSize: '3rem', "& .MuiAlert-icon": {
                            fontSize: '3rem'
                        }
                    }} >
                        <>
                            App no longer allowed to display data
                            <Typography variant="body1" sx={{ fontSize: '1.5rem' }} color={'default'} align="center">
                                R&D agreement is broken. Ask support & extracts over salesforce instead&nbsp;<br/>
                                <Link color="primary" href="https://lectra.my.salesforce.com/" target="_blank" >
                                    Lectra salesforce
                                </Link>{' '}
                            </Typography>
                        </>
                    </Alert>
                </Grid>
            );
        default:
            return (
                /* <React.StrictMode> Commented as this option renders component twice */
                <Provider store={store} >
                    <AuthenticationProvider auth0={{
                        redirectUri: window.location.origin,
                        audience: 'https://cutter.connections.mylectra.com',
                        domain: process.env.REACT_APP_AUTH0_DOMAIN ?? config.auth0Domain,
                        clientId: process.env.REACT_APP_AUTH0_CLIENT_ID ?? config.auth0ClientId,
                        scope: 'openid profile email'
                    }} redirectTo403IfForbidden={true}  >
                        <PersistGate loading={null} persistor={persistedStore}>
                            <ThemedApp />
                        </PersistGate>
                    </AuthenticationProvider>
                </Provider>
                /* </React.StrictMode > */
            );
    }


}

const root = ReactDOM.createRoot(
    document.getElementById('root') as HTMLElement
);

async function bootstrap(): Promise<void> {
    try {
        const res = await fetch('/api/AppSettings/GetPublicConfig');
        const config = await res.json() as PublicConfig;
        root.render(<App config={config} />);
    } catch {
        root.render(<App config={{ auth0Domain: '', auth0ClientId: '' }} />);
    }
}

// Capture the old-domain redirect flag synchronously, before AuthenticationProvider
// redirects to Auth0 and strips query params from the URL.
if (new URLSearchParams(window.location.search).get('ref') === 'old') {
    sessionStorage.setItem('redirectedFromOldDomain', '1');
}

void bootstrap();



// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals

reportWebVitals();
